import { PlusOutlined } from '@ant-design/icons';
import {
  Col,
  Form,
  Grid,
  Image as IMG,
  Input,
  message,
  Row,
  Select,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { Modal } from 'components';
import { NotificationFilterFormValues } from 'features/notification/components/notification-filter-form/type';
import { useCreateSponser } from 'features/sponsers/use-cases/create-sponser';
import { useState } from 'react';

const { useBreakpoint } = Grid;

export const AddSponserModal = ({
  onClose,
  initialValues,
  handleFilter,
  filterForm,
}: {
  onClose: () => void;
  handleFilter: (values: NotificationFilterFormValues) => void;
  initialValues: NotificationFilterFormValues;
  filterForm: any;
}) => {
  const formName = 'add-sponser-form';
  const screens = useBreakpoint();
  const [validateTrigger, setValidateTrigger] = useState(['onSubmit']);
  const [form] = Form.useForm();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const { isLoading, mutate } = useCreateSponser();

  const handleClose = () => {
    onClose();
    document.body.style.overflow = 'visible';
  };

  const handleUpdate = async (formData: any) => {
    try {
      await form.validateFields();

      if (!fileList || fileList.length === 0) {
        form.setFields([
          {
            name: 'ImageFile',
            errors: ['Please upload a banner image!'],
          },
        ]);

        return;
      }

      form.setFields([
        {
          name: 'ImageFile',
          errors: [],
        },
      ]);

      const formDataToSend = new FormData();

      formDataToSend.append('Flag', 'Set');
      formDataToSend.append('name', formData?.name);
      formDataToSend.append('name_arabic', formData?.name_arabic);
      formDataToSend.append('type_id', formData?.type_id);

      // formDataToSend.append('is_active', "true");
      // formDataToSend.append('is_show_on_app', "true");

      if (fileList && fileList.length > 0) {
        const file: any = fileList[0].originFileObj;
        formDataToSend.append('ImageFile', file || null);
      }

      try {
        // Trigger your mutation with FormData
        mutate(formDataToSend, {
          onSuccess: () => {
            filterForm.resetFields();
            handleFilter({ ...initialValues });
            handleClose(); // Close modal or handle success state
          },
          onError: (error: any) => {
            console.error('Error creating notification:', error); // Handle error
          },
        });
      } catch (error) {
        console.error('Error during mutate:', error);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const getBase64 = (file: Blob) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as Blob); // Generate preview for local files
    }
    setPreviewImage(file.url || (file.preview as any)); // Use URL if available; otherwise, use preview
    setPreviewOpen(true);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type='button'>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const checkImageDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const { width, height } = img;
          if (width <= 300 && height <= 300) {
            resolve(true);
          } else {
            message.error(
              `Image dimensions must be minimum 300px (width) x 300px (height). Your image is ${width}px x ${height}px.`,
            );
            resolve(false);
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <Modal
      open
      centered
      className='right-modal cstmmdlright'
      title='Add Sponsor'
      onCancel={handleClose}
      getContainer={false}
      onOk={form.submit}
      okButtonProps={{ loading: isLoading }}
      okText={'Save'}
      cancelButtonProps={{ style: { display: 'none' } }}
      width={screens.lg ? '520px' : '100%'}
    >
      <Form
        className='form_padding'
        id={formName}
        name={formName}
        form={form}
        layout='vertical'
        onFinish={handleUpdate}
        onFinishFailed={() => setValidateTrigger(['onChange'])}
        validateTrigger={validateTrigger}
        disabled={isLoading}
      >
        <figure style={{ padding: '15px 5px' }}>
          <Form.Item
            label='Name'
            name='name'
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Please enter English Name',
              },
            ]}
          >
            <Input placeholder='Enter English Title' maxLength={54} />
          </Form.Item>

          <Form.Item
            label='Name Ar'
            name='name_arabic'
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Please enter Arabic Name',
              },
            ]}
          >
            <Input
              placeholder='Enter Arabic Title'
              maxLength={54}
              dir={'rtl'}
            />
          </Form.Item>
          <Form.Item
            label='Sponser Image'
            name={'type_id'}
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Please Select Sponser Image',
              },
            ]}
          >
            <Select
              style={{ width: '100%' }}
              options={[
                { value: '1', label: 'Normal' },
                { value: '2', label: 'Splash' },
              ]}
              placeholder='Please select sponser image'
            />
          </Form.Item>
          <Row>
            <Col span={12}>
              <Form.Item
                label='Image'
                name='ImageFile'
                help={'Recommend maximum size width: 300px, Height: 300px'}
              >
                <Upload
                  action={undefined}
                  beforeUpload={async (file) => {
                    const isImage = [
                      'image/jpeg',
                      'image/png',
                      'image/jpg',
                    ].includes(file.type);
                    const isSize = file.size <= 20 * 1024; //

                    if (!isImage) {
                      message.error(
                        'You can only upload JPG, JPEG, or PNG files!',
                      );
                    }

                    if (!isSize) {
                      message.error(
                        'You can only upload maximum 20 kb image size',
                      );
                    }

                    const isDimension = await checkImageDimensions(file); // Await the result

                    return isImage && isSize && isDimension
                      ? false
                      : Upload.LIST_IGNORE;
                  }}
                  listType='picture-card'
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  multiple
                  maxCount={1}
                  accept='.jpg,.jpeg,.png'
                >
                  {fileList.length >= 1 ? null : uploadButton}
                </Upload>
                {previewImage && (
                  <IMG
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                      visible: previewOpen,
                      onVisibleChange: setPreviewOpen,
                    }}
                    src={previewImage}
                    alt='Preview Image'
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </figure>
      </Form>
    </Modal>
  );
};
